import { useState } from "react";
import { FixedBundleField, ConfigurableBundleField } from "@codway-dev/ui";
import type { FixedBundleItem, ConfigurableBundleGroup } from "@codway-dev/ui";
import { computeUpsellPrice } from "@codway-dev/shared";
import {
  buildPricingLogic,
  type BundleEditorState,
} from "../../../store/bundle-editor.mapping";
import { useShopCurrency } from "../../../hooks/useShopCurrency";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function FieldPlaceholder({ height = 36 }: { height?: number }) {
  return (
    <div
      className="w-full rounded-md bg-gray-200/60"
      style={{ height: `${height}px` }}
    />
  );
}

// ─── Preview ─────────────────────────────────────────────────────────────────

export function BundleEditorPreview({ state }: { state: BundleEditorState }) {
  const { formatPrice } = useShopCurrency();
  const [fixedSelected, setFixedSelected] = useState(false);
  // Configurable picker: keep selections in local preview state; nothing
  // here mirrors the cart, the merchant is just exploring the UI.
  const [configurableSelections, setConfigurableSelections] = useState<
    Record<string, string[]>
  >({});
  // Preview-only lifecycle state — the parent owns whether the
  // ConfigurableBundleField shows the picker or the summary view.
  const [configurableMode, setConfigurableMode] = useState<
    "picker" | "summary"
  >("picker");

  // Derive bundled total for the preview's pricing strip.
  const derivedFixedBasePrice = state.items.reduce(
    (sum, item) => sum + item.basePrice * item.quantity,
    0
  );
  const effectiveBasePrice =
    state.bundleType === "fixed" ? derivedFixedBasePrice : state.basePrice;
  // Reuse the shared pricing computation (via the same `buildPricingLogic`
  // we send on save) so the preview matches the customer-facing widget
  // to the cent.
  const bundledTotal = computeUpsellPrice(
    buildPricingLogic(state),
    effectiveBasePrice
  );
  const anchorPrice =
    state.compareAtPrice > 0 ? state.compareAtPrice : effectiveBasePrice;
  // Same rule as the shared mapper: suppressed when the merchant flips
  // the hide flag, otherwise only shown when there's a real discount
  // (anchor strictly above the bundled total).
  const showCompareAt = !state.hideCompareAtPrice && bundledTotal < anchorPrice;
  const savings = anchorPrice - bundledTotal;

  const sharedDisplayProps = {
    title: state.titleOverride || undefined,
    description: state.description || undefined,
    imageUrl: state.imageUrl || undefined,
    totalPriceLabel: formatPrice(bundledTotal),
    compareAtLabel: showCompareAt ? formatPrice(anchorPrice) : undefined,
    // Gated on `showCompareAt` (not just `savings > 0`) so the badge
    // hides alongside the strikethrough when the merchant flips the
    // hide flag — the two are presented together to the shopper.
    savingsLabel:
      showCompareAt && savings > 0 ? formatPrice(savings) : undefined,
    styles: {
      backgroundColor: state.backgroundColor || undefined,
      borderColor: state.borderColor || undefined,
      activeBorderColor: state.activeBorderColor || undefined,
      borderRadius: state.borderRadius,
    },
  };

  return (
    <div className="sticky top-4">
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-icon type="view" />
            <s-text type="strong">Vista previa:</s-text>
          </s-stack>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-center text-sm font-bold tracking-wide text-gray-800 mb-4">
              PAGO CONTRA REEMBOLSO
            </div>

            <div className="flex flex-col gap-3">
              <FieldPlaceholder height={48} />

              {state.bundleType === "fixed" && state.items.length > 0 && (
                <FixedBundleField
                  {...sharedDisplayProps}
                  items={state.items.map<FixedBundleItem>((item) => ({
                    id: item._tempId,
                    productTitle: item.productTitle || "Producto",
                    quantity: item.quantity,
                    ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
                    priceLabel: formatPrice(item.basePrice),
                  }))}
                  selected={fixedSelected}
                  onToggle={() => setFixedSelected((s) => !s)}
                />
              )}

              {state.bundleType === "configurable" &&
                state.groups.length > 0 && (
                  <ConfigurableBundleField
                    {...sharedDisplayProps}
                    groups={state.groups.map<ConfigurableBundleGroup>((g) => ({
                      id: g.groupId,
                      name: g.name,
                      pickCount: g.pickCount,
                      options: g.options.map((opt) => ({
                        id: opt._tempId,
                        variantId: opt.variantId,
                        productTitle: opt.productTitle || "Opción",
                        ...(opt.imageUrl ? { imageUrl: opt.imageUrl } : {}),
                      })),
                    }))}
                    selections={configurableSelections}
                    onSelectionChange={(groupId, nextSelections) => {
                      setConfigurableSelections((prev) => ({
                        ...prev,
                        [groupId]: nextSelections,
                      }));
                    }}
                    mode={configurableMode}
                    onAddToCart={() => setConfigurableMode("summary")}
                    onEdit={() => setConfigurableMode("picker")}
                    onRemove={() => {
                      setConfigurableMode("picker");
                      setConfigurableSelections({});
                    }}
                  />
                )}

              <FieldPlaceholder height={48} />
              <FieldPlaceholder height={48} />
              <FieldPlaceholder height={40} />
            </div>
          </div>
        </s-stack>
      </s-box>
    </div>
  );
}
